/**
 * PLACEMENT - where a control goes when it is dragged, resized, dropped on
 * something, or added (2026-10-10, operator: "the moving of items when
 * editing / adding is a bit clunky. it needs to feel smooth and be
 * intuitive" - "do them all").
 *
 * Pure geometry in canvas design px: no Solid, no DOM, no store. The store
 * and EditableItem feed it rectangles and read numbers back, which is what
 * lets `placement_rules` check every rule in Node without a browser.
 *
 * The rules, in one place:
 *   - SNAP. A dragged box snaps to the edges and centres of the other
 *     controls, to a standard GAP beside them, and to the canvas edges and
 *     centre, within a few screen px. An axis with nothing near snaps to the
 *     GRID instead, so free placement still lands on tidy numbers.
 *   - SETTLE. A drop that would overlap something moves to the NEAREST free
 *     spot (a GAP clear of its neighbours where there is room) instead of
 *     jumping back to where it started. Only a drop with no free spot near it
 *     still goes back.
 *   - SPAWN. A new control goes beside the selected one, or beside the last
 *     one added, a GAP away; at a point when the operator pointed at one; at
 *     the top-left margin on an empty page.
 */

export interface Rect { x: number; y: number; w: number; h: number }
export interface Size { w: number; h: number }

/** Grid that free placement rounds to. */
export const GRID = 10;
/** The standard space between two controls: snapped to when dragging beside
 *  a neighbour, kept when spawning or settling. Two grid steps, so a control
 *  placed a GAP from a grid-aligned neighbour is grid-aligned too. */
export const GAP = 20;
/* The app passes the operator's "Space between controls" (Settings > Look,
   2026-10-10, default 0) as `gap`; every function defaults to GAP so the
   pure rules (smoke placement_rules) are stated at one known value. A gap of
   0 drops the spacing targets and the roomy-settle preference: controls then
   line up edge to edge. */
/** How close (in SCREEN px) a line must come before it snaps. Callers divide
 *  by the canvas scale so the feel is the same at any zoom. */
export const SNAP_SCREEN_PX = 8;
/** Where the first control on an empty page goes (the pre-2026-10-10 spawn
 *  point, kept so an empty page starts where it always did). */
export const MARGIN = 40;
/** How far a blocked drop may travel to find room before it gives up and
 *  goes back to where it started. */
export const SETTLE_RADIUS = 360;

/** A snap guide line to draw: a vertical line (`axis: 'x'`) at x = pos from
 *  y = from to y = to, or a horizontal one. `gap` marks a spacing snap. */
export interface Guide { axis: 'x' | 'y'; pos: number; from: number; to: number; gap?: boolean }

export function boundsOf(rects: readonly Rect[]): Rect {
  const x = Math.min(...rects.map((r) => r.x));
  const y = Math.min(...rects.map((r) => r.y));
  const r = Math.max(...rects.map((q) => q.x + q.w));
  const b = Math.max(...rects.map((q) => q.y + q.h));
  return { x, y, w: r - x, h: b - y };
}

/** True when `r`, grown by `pad` on every side, overlaps any obstacle.
 *  Strict: two boxes exactly `pad` apart do NOT overlap, so a control placed
 *  exactly a GAP from a neighbour passes a GAP-padded test. */
export function overlapsAny(r: Rect, obstacles: readonly Rect[], pad = 0): boolean {
  const x0 = r.x - pad, y0 = r.y - pad, x1 = r.x + r.w + pad, y1 = r.y + r.h + pad;
  for (const o of obstacles) {
    if (x0 < o.x + o.w && x1 > o.x && y0 < o.y + o.h && y1 > o.y) return true;
  }
  return false;
}

function inBounds(r: Rect, bounds: Size): boolean {
  return r.x >= 0 && r.y >= 0 && r.x + r.w <= bounds.w && r.y + r.h <= bounds.h;
}

/** Do two ranges [a0,a1] and [b0,b1] overlap (touching counts)? */
function spans(a0: number, a1: number, b0: number, b1: number): boolean {
  return a0 <= b1 && a1 >= b0;
}

/** `gap` is set on a spacing target: the [from, to] span of the gap itself. */
interface Target { line: number; value: number; obstacle: Rect | null; gap: [number, number] | null }

/**
 * The lines one axis of a moving box may snap to. `lines` are the moving
 * box's own left/centre/right (or top/middle/bottom) at its proposed spot;
 * the result pairs each with every value it may land on.
 */
function axisTargets(
  axis: 'x' | 'y', box: Rect, obstacles: readonly Rect[], bounds: Size, gap: number,
): Target[] {
  const lo = axis === 'x' ? box.x : box.y;
  const len = axis === 'x' ? box.w : box.h;
  const hi = lo + len;
  const mid = lo + len / 2;
  const plo = axis === 'x' ? box.y : box.x;
  const phi = plo + (axis === 'x' ? box.h : box.w);
  const extent = axis === 'x' ? bounds.w : bounds.h;
  const out: Target[] = [
    { line: lo, value: 0, obstacle: null, gap: null },
    { line: hi, value: extent, obstacle: null, gap: null },
    { line: mid, value: extent / 2, obstacle: null, gap: null },
  ];
  for (const o of obstacles) {
    const olo = axis === 'x' ? o.x : o.y;
    const olen = axis === 'x' ? o.w : o.h;
    const ohi = olo + olen;
    const omid = olo + olen / 2;
    // Edge and centre alignment, to any control on the page.
    out.push(
      { line: lo, value: olo, obstacle: o, gap: null },
      { line: lo, value: ohi, obstacle: o, gap: null },
      { line: hi, value: ohi, obstacle: o, gap: null },
      { line: hi, value: olo, obstacle: o, gap: null },
      { line: mid, value: omid, obstacle: o, gap: null },
    );
    // The standard gap, only beside a control the box is level with: a gap
    // to something on the far side of the page means nothing.
    const opl = axis === 'x' ? o.y : o.x;
    const oph = opl + (axis === 'x' ? o.h : o.w);
    if (gap > 0 && spans(plo, phi, opl, oph)) {
      out.push(
        { line: lo, value: ohi + gap, obstacle: o, gap: [ohi, ohi + gap] },
        { line: hi, value: olo - gap, obstacle: o, gap: [olo - gap, olo] },
      );
    }
  }
  return out;
}

/** Best shift for one axis: the nearest target within `threshold`, else the
 *  grid. Returns the shift to add and the guides it matched. */
function snapAxis(
  axis: 'x' | 'y', box: Rect, obstacles: readonly Rect[], bounds: Size, threshold: number, gap: number,
): { shift: number; guides: Guide[] } {
  const targets = axisTargets(axis, box, obstacles, bounds, gap);
  let best: number | null = null;
  for (const t of targets) {
    const d = t.value - t.line;
    if (Math.abs(d) <= threshold && (best === null || Math.abs(d) < Math.abs(best))) best = d;
  }
  if (best === null) {
    const lo = axis === 'x' ? box.x : box.y;
    return { shift: Math.round(lo / GRID) * GRID - lo, guides: [] };
  }
  // Every target the snapped box now sits exactly on gets a guide, so two
  // neighbours aligned at once both show.
  const guides: Guide[] = [];
  const shifted: Rect = axis === 'x' ? { ...box, x: box.x + best } : { ...box, y: box.y + best };
  const pFrom = axis === 'x' ? shifted.y : shifted.x;
  const pTo = pFrom + (axis === 'x' ? shifted.h : shifted.w);
  for (const t of targets) {
    if (Math.abs(t.value - (t.line + best)) > 0.5) continue;
    let from = pFrom, to = pTo;
    if (t.obstacle) {
      const o = t.obstacle;
      from = Math.min(from, axis === 'x' ? o.y : o.x);
      to = Math.max(to, axis === 'x' ? o.y + o.h : o.x + o.w);
    } else {
      from = 0;
      to = axis === 'x' ? bounds.h : bounds.w;
    }
    // A gap guide is drawn ACROSS the gap (on the other axis), at the middle
    // of the overlap between the two boxes, so it reads as a spacer.
    if (t.gap && t.obstacle) {
      const o = t.obstacle;
      const a0 = Math.max(pFrom, axis === 'x' ? o.y : o.x);
      const a1 = Math.min(pTo, axis === 'x' ? o.y + o.h : o.x + o.w);
      guides.push({ axis: axis === 'x' ? 'y' : 'x', pos: (a0 + a1) / 2, from: t.gap[0], to: t.gap[1], gap: true });
      continue;
    }
    guides.push({ axis, pos: t.value, from, to });
  }
  return { shift: best, guides };
}

/**
 * Snap a moving box. `box` is where it would be at the raw pointer delta;
 * the result is the extra shift to add on each axis plus the guides to draw.
 */
export function snapBox(
  box: Rect, obstacles: readonly Rect[], bounds: Size, threshold: number, gap = GAP,
): { sx: number; sy: number; guides: Guide[] } {
  const x = snapAxis('x', box, obstacles, bounds, threshold, gap);
  const y = snapAxis('y', { ...box, x: box.x + x.shift }, obstacles, bounds, threshold, gap);
  return { sx: x.shift, sy: y.shift, guides: [...x.guides, ...y.guides] };
}

/**
 * Snap ONE moving edge (a resize). `axis` is the edge's axis, `value` where
 * the pointer puts it, `side` which edge of the box it is. `along` is the
 * box's extent on the other axis, so a gap is only offered beside a control
 * the box is level with.
 */
export function snapEdge(
  axis: 'x' | 'y', value: number, side: 'lo' | 'hi', along: [number, number],
  obstacles: readonly Rect[], bounds: Size, threshold: number, gap = GAP,
): { value: number; guides: Guide[] } {
  const cands: { v: number; o: Rect | null; gap: boolean }[] = [
    { v: 0, o: null, gap: false },
    { v: axis === 'x' ? bounds.w : bounds.h, o: null, gap: false },
  ];
  for (const o of obstacles) {
    const olo = axis === 'x' ? o.x : o.y;
    const ohi = olo + (axis === 'x' ? o.w : o.h);
    cands.push({ v: olo, o, gap: false }, { v: ohi, o, gap: false });
    const opl = axis === 'x' ? o.y : o.x;
    const oph = opl + (axis === 'x' ? o.h : o.w);
    if (gap > 0 && spans(along[0], along[1], opl, oph)) {
      cands.push(side === 'lo' ? { v: ohi + gap, o, gap: true } : { v: olo - gap, o, gap: true });
    }
  }
  let best: { v: number; o: Rect | null; gap: boolean } | null = null;
  for (const c of cands) {
    if (Math.abs(c.v - value) <= threshold && (best === null || Math.abs(c.v - value) < Math.abs(best.v - value))) best = c;
  }
  if (!best) return { value: Math.round(value / GRID) * GRID, guides: [] };
  const o = best.o;
  let from = along[0], to = along[1];
  if (o) {
    from = Math.min(from, axis === 'x' ? o.y : o.x);
    to = Math.max(to, axis === 'x' ? o.y + o.h : o.x + o.w);
  } else {
    from = 0;
    to = axis === 'x' ? bounds.h : bounds.w;
  }
  return { value: best.v, guides: best.gap ? [] : [{ axis, pos: best.v, from, to }] };
}

// Offsets sorted by distance, cached per (step, radius): the search walks
// them nearest-first and stops at the first that fits.
const offsetCache = new Map<string, [number, number][]>();
function offsetsNearestFirst(step: number, radius: number): [number, number][] {
  const key = `${step}:${radius}`;
  const hit = offsetCache.get(key);
  if (hit) return hit;
  const n = Math.floor(radius / step);
  const out: [number, number][] = [];
  for (let i = -n; i <= n; i++) {
    for (let j = -n; j <= n; j++) {
      const dx = i * step, dy = j * step;
      if (dx * dx + dy * dy <= radius * radius) out.push([dx, dy]);
    }
  }
  out.sort((a, b) => (a[0] ** 2 + a[1] ** 2) - (b[0] ** 2 + b[1] ** 2));
  offsetCache.set(key, out);
  return out;
}

/**
 * The smallest shift that puts every rect in `set` inside the canvas and clear
 * of every obstacle. A spot a GAP clear of its neighbours is preferred, but
 * only while it is not much further than the nearest spot that merely does
 * not touch: a drop should land near where it was let go, not leap across
 * the page for tidiness. Null when nothing within `radius` fits.
 */
export function nearestFreeShift(
  set: readonly Rect[], obstacles: readonly Rect[], bounds: Size, radius = SETTLE_RADIUS, gap = GAP,
): { dx: number; dy: number } | null {
  const offsets = offsetsNearestFirst(GRID, radius);
  const first = (pad: number): [number, number] | null => {
    for (const o of offsets) {
      let ok = true;
      for (const r of set) {
        const m = { x: r.x + o[0], y: r.y + o[1], w: r.w, h: r.h };
        if (!inBounds(m, bounds) || overlapsAny(m, obstacles, pad)) { ok = false; break; }
      }
      if (ok) return o;
    }
    return null;
  };
  const tight = first(0);
  if (!tight) return null;
  const roomy = gap > 0 ? first(gap) : null;
  const dist = (o: [number, number]) => Math.hypot(o[0], o[1]);
  const pick = roomy && dist(roomy) <= dist(tight) + 2 * gap ? roomy : tight;
  return { dx: pick[0], dy: pick[1] };
}

/**
 * Where a NEW control of `size` goes.
 *   - `point`: the operator pointed at a spot (the canvas menu's "+ Item",
 *     a tile dragged out of the palette). Centred on it if it fits, else the
 *     nearest room to it.
 *   - `anchor`: the selected control, else the last one added. Beside it a
 *     GAP away - right, then below, then left, then above - else the nearest
 *     room to it.
 *   - neither (an empty page): the top-left margin.
 * Falls back to a scan of the whole canvas, then to the margin.
 */
export function spawnSpot(
  size: Size, obstacles: readonly Rect[], bounds: Size,
  opts: { point?: { x: number; y: number } | null; anchor?: Rect | null; gap?: number } = {},
): { x: number; y: number } {
  const gap = opts.gap ?? GAP;
  const fits = (x: number, y: number, pad: number) => {
    const r = { x, y, w: size.w, h: size.h };
    return inBounds(r, bounds) && !overlapsAny(r, obstacles, pad);
  };
  const snap = (v: number) => Math.round(v / GRID) * GRID;
  const clampX = (x: number) => Math.max(0, Math.min(bounds.w - size.w, x));
  const clampY = (y: number) => Math.max(0, Math.min(bounds.h - size.h, y));

  if (opts.point) {
    const x = clampX(snap(opts.point.x - size.w / 2));
    const y = clampY(snap(opts.point.y - size.h / 2));
    if (fits(x, y, 0)) return { x, y };
    const s = nearestFreeShift([{ x, y, ...size }], obstacles, bounds, Math.max(bounds.w, bounds.h), gap);
    if (s) return { x: x + s.dx, y: y + s.dy };
  }
  const a = opts.anchor;
  if (a) {
    // The row the anchor sits in: every control level with it. When the row
    // is full the next one starts a new row underneath, like text wrapping.
    const row = obstacles.filter((o) => o.y < a.y + a.h && o.y + o.h > a.y);
    const rowLeft = Math.min(a.x, ...row.map((o) => o.x));
    const rowBottom = Math.max(a.y + a.h, ...row.map((o) => o.y + o.h));
    const tries: [number, number][] = [
      [a.x + a.w + gap, a.y],
      [rowLeft, rowBottom + gap],
      [a.x, a.y + a.h + gap],
      [a.x - gap - size.w, a.y],
      [a.x, a.y - gap - size.h],
    ];
    for (const [x, y] of tries) if (fits(x, y, gap)) return { x, y };
    // No room right beside it: the nearest room to the anchor itself.
    const s = nearestFreeShift([{ x: clampX(a.x), y: clampY(a.y), ...size }], obstacles, bounds, Math.max(bounds.w, bounds.h), gap);
    if (s) return { x: clampX(a.x) + s.dx, y: clampY(a.y) + s.dy };
  }
  if (fits(MARGIN, MARGIN, 0)) return { x: MARGIN, y: MARGIN };
  for (const pad of [gap, 0]) {
    for (let y = MARGIN % GRID; y + size.h <= bounds.h; y += GRID) {
      for (let x = MARGIN % GRID; x + size.w <= bounds.w; x += GRID) {
        if (fits(x, y, pad)) return { x, y };
      }
    }
  }
  return { x: clampX(MARGIN), y: clampY(MARGIN) };
}
